import React from 'react';
import { Paperclip, Wand2, Mic, CornerDownLeft, Square } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useAgentStore } from '@/stores/agentStore';
import { AgentModelSelector } from '../AgentModelSelector';
import { SearchModeSelector } from './SearchModeSelector';
import { ReferenceThumbnails } from './AIReplyBlock';
import type { AttachmentItem } from '../types';

interface MessageInputProps {
  attachments: AttachmentItem[];
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onSend: () => void;
  onStop: () => void;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveAttachment: (index: number) => void;
}

export const MessageInput = React.memo(({
  attachments,
  textareaRef,
  fileInputRef,
  onSend,
  onStop,
  onFileSelect,
  onRemoveAttachment,
}: MessageInputProps) => {
  const { input, isLoading } = useAgentStore(useShallow((s) => ({
    input: s.input,
    isLoading: s.isLoading,
  })));

  const handleInputChange = (value: string) => {
    useAgentStore.getState().setInput(value);
  };

  return (
    <div className="px-4 pb-4 pt-2 shrink-0">
      <div className="bg-[#252528] rounded-2xl border border-white/10 p-3">
        <ReferenceThumbnails attachments={attachments} onRemoveAttachment={onRemoveAttachment} />

        <textarea
          ref={textareaRef}
          className="w-full bg-transparent text-[13px] text-white placeholder-slate-400 focus:outline-none resize-none custom-scrollbar leading-5"
          placeholder="描述想法，或框选节点添加上下文..."
          value={input}
          onChange={e => handleInputChange(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onSend();
            }
          }}
          rows={3}
          style={{ minHeight: '60px', maxHeight: '120px' }}
        />

        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-1 text-white hover:bg-white/10 transition-colors px-2 py-1 rounded-md"
            >
              <Paperclip size={14} />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept="image/*,video/*,audio/*,.pdf,.txt,.md"
              multiple
              onChange={onFileSelect}
            />
            {attachments.length > 0 && (
              <span className="text-[10px] text-cyan-400 ml-1">{attachments.length}</span>
            )}
            <div className="w-px h-4 bg-white/10 mx-1" />
            <AgentModelSelector />
            <div className="w-px h-4 bg-white/10 mx-1" />
            <SearchModeSelector />
          </div>
          <div className="flex items-center">
            <button className="flex items-center gap-1 text-white hover:bg-white/10 transition-colors px-2 py-1 rounded-md">
              <Wand2 size={14} />
            </button>
            <div className="w-px h-4 bg-white/10 mx-1" />
            <button className="flex items-center gap-1 text-white hover:bg-white/10 transition-colors px-2 py-1 rounded-md">
              <Mic size={14} />
            </button>
            <div className="w-px h-4 bg-white/10 mx-1" />
            <button
              onClick={isLoading ? onStop : onSend}
              className={`w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300 ${
                isLoading
                  ? 'bg-white text-black hover:bg-slate-200'
                  : input.trim()
                    ? 'bg-white text-black hover:bg-slate-200 hover:scale-105'
                    : 'bg-white/10 text-slate-600 cursor-not-allowed'
              }`}
              disabled={!isLoading && !input.trim()}
            >
              {isLoading ? <Square size={8} fill="currentColor" /> : <CornerDownLeft size={12} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});
